import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import {
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  resetPasswordWithOtp,
  clearError,
} from '../../store/slices/authSlice';

const steps = ['Enter Email', 'Verify OTP', 'Reset Password'];

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetDone, setResetDone] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startResendCooldown = () => {
    setResendCooldown(60);
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const validateEmail = (val) => {
    if (!val) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (val) => {
    if (!val) return 'Password is required';
    if (val.length < 6) return 'Password must be at least 6 characters';
    return '';
  };

  // Step 1 — send OTP
  const handleSendOtp = async () => {
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }
    setEmailError('');
    try {
      await dispatch(sendForgotPasswordOtp(email)).unwrap();
      toast.success('OTP sent to your email address');
      startResendCooldown();
      setActiveStep(1);
    } catch (e) {
      // error shown via effect
    }
  };

  // Step 1 — resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await dispatch(sendForgotPasswordOtp(email)).unwrap();
      toast.success('OTP resent to your email address');
      startResendCooldown();
      setOtp('');
      setOtpError('');
    } catch (e) {
      // error shown via effect
    }
  };

  // Step 2 — verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) { setOtpError('Please enter the 6-digit OTP'); return; }
    setOtpError('');
    try {
      await dispatch(verifyForgotPasswordOtp({ email, otp })).unwrap();
      toast.success('OTP verified successfully');
      setActiveStep(2);
    } catch (e) {
      // error shown via effect
    }
  };

  // Step 3 — reset password
  const handleResetPassword = async () => {
    const pErr = validatePassword(newPassword);
    const cErr = newPassword !== confirmPassword ? 'Passwords do not match' : '';
    setPasswordError(pErr);
    setConfirmPasswordError(cErr);
    if (pErr || cErr) return;

    try {
      await dispatch(resetPasswordWithOtp({ email, otp, newPassword })).unwrap();
      setResetDone(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) {
      // error shown via effect
    }
  };

  if (resetDone) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={8} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} mb={1}>Password Reset Successful!</Typography>
          <Typography color="text.secondary" mb={3}>
            Your password has been updated. Redirecting to login...
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            sx={{ background: 'linear-gradient(45deg, #2C3E50, #34495E)' }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <Paper elevation={8} sx={{ p: 4, borderRadius: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1,
                background: 'linear-gradient(45deg, #2C3E50, #E67E22)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Forgot Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Reset your MOS Marketplace password
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {/* Step 0: Email */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter your registered email address and we'll send you a 6-digit OTP.
              </Typography>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Email color="action" /></InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                onClick={handleSendOtp}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #2C3E50, #34495E)',
                  '&:hover': { background: 'linear-gradient(45deg, #1A252F, #2C3E50)' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
              </Button>
            </Box>
          )}

          {/* Step 1: OTP Verification */}
          {activeStep === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                A 6-digit OTP has been sent to <strong>{email}</strong>. It expires in 5 minutes.
              </Alert>
              <TextField
                fullWidth
                label="Enter OTP"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                error={!!otpError}
                helperText={otpError}
                inputProps={{ maxLength: 6, style: { letterSpacing: 8, fontSize: 24, textAlign: 'center' } }}
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                onClick={handleVerifyOtp}
                sx={{
                  py: 1.5,
                  mb: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #2C3E50, #34495E)',
                  '&:hover': { background: 'linear-gradient(45deg, #1A252F, #2C3E50)' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Didn't receive the OTP?{' '}
                  {resendCooldown > 0 ? (
                    <span>Resend in {resendCooldown}s</span>
                  ) : (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleResendOtp}
                      disabled={loading}
                      sx={{ cursor: 'pointer' }}
                    >
                      Resend OTP
                    </Link>
                  )}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Step 2: New Password */}
          {activeStep === 2 && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>OTP verified. Set your new password.</Alert>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordError(''); }}
                error={!!passwordError}
                helperText={passwordError}
                sx={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError(''); }}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                onClick={handleResetPassword}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #2C3E50, #34495E)',
                  '&:hover': { background: 'linear-gradient(45deg, #1A252F, #2C3E50)' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
            </Box>
          )}

          {/* Back to Login */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{' '}
              <Link component={RouterLink} to="/login" sx={{ textDecoration: 'none' }}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default ForgotPasswordPage;
